import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Loader2, 
  ChevronDown,
  AlertOctagon
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface HardGate {
  type: "archetype_violation" | "logical_contradiction";
  message: string;
  criterion: string;
  currentValue: string;
  requiredAction: "change_score" | "change_archetype";
  suggestion: string;
}

interface SoftGate {
  type: "potential_inconsistency" | "missing_differentiation" | "all_medium";
  message: string;
  criterion?: string;
}

interface ValidationResult {
  status: "valid" | "warning" | "blocked";
  hardGates: HardGate[];
  softGates: SoftGate[];
  confirmations: string[];
}

interface LogicGateValidatorProps {
  sessionId: string;
  optionId: string;
  optionName: string;
  archetype: string;
  scores: Record<string, string>;
  isComplete: boolean;
  onBlockingGate?: (isBlocked: boolean) => void;
  onResolveGate?: (criterion: string) => void;
}

export const LogicGateValidator = ({ 
  sessionId, 
  optionId, 
  optionName,
  archetype,
  scores, 
  isComplete,
  onBlockingGate,
  onResolveGate
}: LogicGateValidatorProps) => {
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBlockingDialog, setShowBlockingDialog] = useState(false);
  const [selectedGate, setSelectedGate] = useState<HardGate | null>(null);

  useEffect(() => {
    if (isComplete) {
      validateColumn();
    }
  }, [isComplete, JSON.stringify(scores)]);

  useEffect(() => {
    if (validation) {
      onBlockingGate?.(validation.status === "blocked");
      // Auto-show dialog for blocking gates
      if (validation.status === "blocked" && validation.hardGates.length > 0) {
        setSelectedGate(validation.hardGates[0]);
        setShowBlockingDialog(true);
      }
    }
  }, [validation]);

  const validateColumn = async () => {
    setIsValidating(true);
    try {
      const { data, error } = await supabase.functions.invoke("phase3-validate-logic-gate", {
        body: { 
          session_id: sessionId, 
          option_id: optionId,
          archetype,
          scores 
        },
      });

      if (error) throw error;
      setValidation(data);
    } catch (error) {
      console.error("Error validating logic gates:", error);
      setValidation({
        status: "valid",
        hardGates: [],
        softGates: [],
        confirmations: ["Bewertung erfasst ✓"]
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleResolveGate = (gate: HardGate) => {
    setShowBlockingDialog(false);
    onResolveGate?.(gate.criterion);
  };

  if (!isComplete) return null;

  if (isValidating) {
    return (
      <div className="flex items-center justify-center gap-2 py-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Prüfe Konsistenz...</span>
      </div>
    );
  }

  if (!validation) return null;

  const { status, hardGates, softGates, confirmations } = validation;
  
  // Status-based styling - replace "Stimmig" with conflict-aware language
  const statusConfig = {
    blocked: {
      icon: XCircle,
      color: "text-destructive",
      bg: "bg-destructive/10 border-destructive/30",
      label: "K.O.-Konflikt"
    },
    warning: {
      icon: AlertTriangle,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800",
      label: `${softGates.length} Inkonsistenz${softGates.length > 1 ? 'en' : ''}`
    },
    valid: {
      icon: CheckCircle2,
      color: "text-muted-foreground",
      bg: "bg-muted/50 border-border",
      label: "Konsistent"
    }
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className={cn("rounded-lg border transition-all", config.bg)}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "w-full justify-between px-3 py-2 h-auto hover:bg-transparent",
                config.color
              )}
            >
              <div className="flex items-center gap-2">
                <StatusIcon className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">{config.label}</span>
              </div>
              <ChevronDown className={cn(
                "h-3 w-3 transition-transform",
                isExpanded && "rotate-180"
              )} />
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="px-3 pb-3 space-y-2 border-t border-inherit">
              {/* Hard Gates (Blocking) */}
              {hardGates.map((gate, i) => (
                <div 
                  key={`hg-${i}`} 
                  className="flex items-start gap-2 text-xs text-destructive pt-2"
                >
                  <AlertOctagon className="h-3 w-3 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <span className="leading-relaxed font-medium">{gate.message}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-6 text-xs mt-1"
                      onClick={() => {
                        setSelectedGate(gate);
                        setShowBlockingDialog(true);
                      }}
                    >
                      Auflösen
                    </Button>
                  </div>
                </div>
              ))}

              {/* Soft Gates (Warnings) */}
              {softGates.map((gate, i) => (
                <div 
                  key={`sg-${i}`} 
                  className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 pt-2"
                >
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{gate.message}</span>
                </div>
              ))}

              {/* Confirmations */}
              {confirmations.map((msg, i) => (
                <div 
                  key={`c-${i}`} 
                  className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-400 pt-2"
                >
                  <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{msg}</span>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Blocking Gate Dialog */}
      <Dialog open={showBlockingDialog} onOpenChange={setShowBlockingDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertOctagon className="h-5 w-5" />
              Logischer Konflikt
            </DialogTitle>
            <DialogDescription className="pt-2">
              {selectedGate?.message}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-1">Aktuell:</p>
              <p className="text-sm text-muted-foreground">
                {selectedGate?.criterion}: <strong>{selectedGate?.currentValue}</strong>
              </p>
            </div>
            
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-sm font-medium text-primary mb-1">Empfehlung:</p>
              <p className="text-sm">{selectedGate?.suggestion}</p>
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowBlockingDialog(false)}
            >
              Später
            </Button>
            <Button 
              onClick={() => selectedGate && handleResolveGate(selectedGate)}
            >
              Wert ändern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
